/**
 * Curated, patient-facing financial guidance.
 *
 * Every material claim carries its own provenance because provider pages can
 * stay online while rates, fees, limits, or eligibility change underneath.
 * Keep this registry versioned until a reviewed content service exists.
 */

export type FinancialClaimKind =
  | "summary"
  | "promotion"
  | "apr"
  | "loan-maximum"
  | "fee"
  | "term"
  | "credit-check"
  | "eligibility"
  | "availability"
  | "qualifying-expense"
  | "threshold"
  | "timing"
  | "account-rule";

export type FinancialClaimProvenance = {
  sourceUrl: string;
  jurisdiction: string;
  effectiveYear?: number;
  effectiveDate?: string;
  lastVerifiedOn: string;
  reviewExpiresOn: string;
  reviewedBy: string;
  owner: string;
};

export type FinancialClaim = {
  id: string;
  kind: FinancialClaimKind;
  label: string;
  value: string;
  caveat?: string;
  provenance: FinancialClaimProvenance;
};

export type FinancingProductType =
  | "fertility-financing"
  | "healthcare-financing"
  | "fertility-insurance"
  | "personal-loan";

export type FinancingProvider = {
  id: string;
  name: string;
  productType: FinancingProductType;
  productLabel: string;
  url: string;
  summary: FinancialClaim;
  claims: FinancialClaim[];
};

export type TaxBenefitIcon = "taxes" | "piggyBank" | "babyBottle";

export type TaxBenefit = {
  id: string;
  icon: TaxBenefitIcon;
  title: string;
  subtitle: string;
  summary: FinancialClaim;
  eligible: FinancialClaim[];
  note: string;
};

const PROVIDER_REVIEW = {
  jurisdiction: "US",
  lastVerifiedOn: "2026-07-29",
  reviewExpiresOn: "2026-10-29",
  reviewedBy: "Product & Content",
  owner: "Costs content",
} as const;

const TAX_REVIEW = {
  jurisdiction: "US federal",
  lastVerifiedOn: "2026-07-28",
  reviewExpiresOn: "2026-12-31",
  reviewedBy: "Product & Content",
  owner: "Costs content",
} as const;

function providerClaim(
  claim: Omit<FinancialClaim, "provenance"> & {
    sourceUrl: string;
    effectiveDate?: string;
  },
): FinancialClaim {
  const { sourceUrl, effectiveDate, ...claimData } = claim;
  return {
    ...claimData,
    provenance: {
      ...PROVIDER_REVIEW,
      sourceUrl,
      effectiveDate: effectiveDate ?? PROVIDER_REVIEW.lastVerifiedOn,
    },
  };
}

function taxClaim(
  claim: Omit<FinancialClaim, "provenance"> & {
    sourceUrl: string;
    effectiveYear: number;
  },
): FinancialClaim {
  const { sourceUrl, effectiveYear, ...claimData } = claim;
  return {
    ...claimData,
    provenance: {
      ...TAX_REVIEW,
      sourceUrl,
      effectiveYear,
    },
  };
}

export const FINANCING_PROVIDERS: FinancingProvider[] = [
  {
    id: "capexmd",
    name: "CapexMD",
    productType: "fertility-financing",
    productLabel: "Fertility financing",
    url: "https://www.capexmd.com",
    summary: providerClaim({
      id: "capexmd-summary",
      kind: "summary",
      label: "What it is",
      value: "Fertility financing for reproductive care.",
      caveat: "Current rates, fees, terms, and clinic availability vary.",
      sourceUrl: "https://www.capexmd.com",
    }),
    claims: [
      providerClaim({
        id: "capexmd-loan-maximum",
        kind: "loan-maximum",
        label: "Loan amount",
        value: "$3,000–$50,000.",
        caveat:
          "Approval and the amount offered depend on creditworthiness, income, ability to repay, and other factors.",
        sourceUrl: "https://www.capexmd.com/",
      }),
      providerClaim({
        id: "capexmd-apr",
        kind: "apr",
        label: "APR",
        value: "8.99%–24.99% fixed APR.",
        caveat: "Your final rate depends on the offer you receive.",
        sourceUrl: "https://www.capexmd.com/calculator.htm",
      }),
      providerClaim({
        id: "capexmd-term",
        kind: "term",
        label: "Repayment term",
        value: "Six to 84 months.",
        sourceUrl: "https://www.capexmd.com/calculator.htm",
      }),
      providerClaim({
        id: "capexmd-credit-check",
        kind: "credit-check",
        label: "Credit check",
        value:
          "Soft inquiry for the application; CapexMD says a hard inquiry is run when a loan is approved and funded.",
        sourceUrl: "https://www.capexmd.com/calculator.htm",
      }),
      providerClaim({
        id: "capexmd-timing",
        kind: "timing",
        label: "Timing",
        value:
          "Online application takes about five minutes; same-day review is typical.",
        sourceUrl: "https://www.capexmd.com/",
      }),
      providerClaim({
        id: "capexmd-availability",
        kind: "availability",
        label: "Availability",
        value:
          "Confirm current offers and clinic participation directly with CapexMD.",
        sourceUrl: "https://www.capexmd.com",
      }),
      providerClaim({
        id: "capexmd-eligibility",
        kind: "eligibility",
        label: "Approval",
        value: "Eligibility, rates, terms, and fees vary by application.",
        sourceUrl: "https://www.capexmd.com",
      }),
    ],
  },
  {
    id: "patientfi",
    name: "PatientFi",
    productType: "healthcare-financing",
    productLabel: "Healthcare financing",
    url: "https://patientfi.com",
    summary: providerClaim({
      id: "patientfi-summary",
      kind: "summary",
      label: "What it is",
      value: "Healthcare financing with promotional and fixed-rate options.",
      caveat:
        "Terms depend on approval and the offer available through your provider.",
      sourceUrl: "https://patientfi.com/how-it-works/",
    }),
    claims: [
      providerClaim({
        id: "patientfi-promotion",
        kind: "promotion",
        label: "Promotional offer",
        value: "0% interest if paid in full during the promotional period.",
        caveat:
          "Interest is charged during the promotional period and may not be waived if the balance is not paid in full on time.",
        sourceUrl: "https://patientfi.com/disclosures/",
      }),
      providerClaim({
        id: "patientfi-apr",
        kind: "apr",
        label: "Fixed-rate plans",
        value: "APR starts at 6.99% for approved applicants.",
        caveat:
          "The advertised starting rate assumes excellent credit and recurring payments; your actual APR may be higher.",
        sourceUrl: "https://patientfi.com/how-it-works/",
      }),
      providerClaim({
        id: "patientfi-credit-check",
        kind: "credit-check",
        label: "Credit check",
        value: "Applying has no impact on your credit score.",
        caveat: "A credit decision and final approval are still required.",
        sourceUrl: "https://patientfi.com/how-it-works/",
      }),
      providerClaim({
        id: "patientfi-application-details",
        kind: "eligibility",
        label: "Application details",
        value:
          "Have your date of birth, last four SSN digits, physical address, yearly gross income, and monthly housing cost ready.",
        caveat:
          "Louise will never ask you to enter your SSN or financial account numbers here.",
        sourceUrl:
          "https://support.patientfi.com/hc/en-us/articles/42674904841747-What-Do-I-Need-to-Apply",
      }),
      providerClaim({
        id: "patientfi-availability",
        kind: "availability",
        label: "Provider access",
        value:
          "You need to use a PatientFi partner provider; an approved spending limit is valid for 120 days.",
        sourceUrl:
          "https://support.patientfi.com/hc/en-us/articles/42784546874771-How-PatientFi-Works",
      }),
      providerClaim({
        id: "patientfi-timing",
        kind: "timing",
        label: "Decision timing",
        value:
          "PatientFi describes an online application with an instant credit decision.",
        sourceUrl:
          "https://support.patientfi.com/hc/en-us/articles/42784546874771-How-PatientFi-Works",
      }),
    ],
  },
  {
    id: "gaia",
    name: "Gaia",
    productType: "fertility-insurance",
    productLabel: "Fertility insurance",
    url: "https://gaiafamily.com/en-us",
    summary: providerClaim({
      id: "gaia-summary",
      kind: "summary",
      label: "What it is",
      value:
        "Fertility insurance plans that may cover IVF and other treatments.",
      caveat:
        "Coverage and availability vary by plan, location, and eligibility.",
      sourceUrl: "https://gaiafamily.com/en-us",
    }),
    claims: [
      providerClaim({
        id: "gaia-availability",
        kind: "availability",
        label: "Availability",
        value:
          "Confirm current plan availability and covered services with Gaia.",
        sourceUrl: "https://gaiafamily.com/en-us",
      }),
      providerClaim({
        id: "gaia-eligibility",
        kind: "eligibility",
        label: "Eligibility",
        value:
          "Plan eligibility, premiums, and covered treatment vary by applicant.",
        sourceUrl: "https://gaiafamily.com/en-us",
      }),
    ],
  },
  {
    id: "future-family",
    name: "Future Family",
    productType: "fertility-financing",
    productLabel: "Fertility financing",
    url: "https://www.futurefamily.com",
    summary: providerClaim({
      id: "future-family-summary",
      kind: "summary",
      label: "What it is",
      value: "Fertility financing plans with plan-specific monthly payments.",
      caveat:
        "Current plan terms, fees, and eligibility must be confirmed with Future Family.",
      sourceUrl: "https://www.futurefamily.com",
    }),
    claims: [
      providerClaim({
        id: "future-family-loan-maximum",
        kind: "loan-maximum",
        label: "Loan amount",
        value: "Up to $50,000.",
        caveat:
          "The plan amount is customized to your treatment costs and eligibility.",
        sourceUrl: "https://www.futurefamily.com/",
      }),
      providerClaim({
        id: "future-family-apr",
        kind: "apr",
        label: "APR",
        value: "9.74%–23.74% APR.",
        caveat:
          "Your rate depends on credit score, income, loan amount, term, and credit history.",
        sourceUrl: "https://www.futurefamily.com/faq",
      }),
      providerClaim({
        id: "future-family-credit-check",
        kind: "credit-check",
        label: "Credit check",
        value:
          "Prequalification does not affect your credit score; a hard inquiry may be required to move forward.",
        sourceUrl: "https://www.futurefamily.com/faq",
      }),
      providerClaim({
        id: "future-family-timing",
        kind: "timing",
        label: "Timing",
        value:
          "Pre-approval takes about two minutes; funding may be available within 24 hours after the agreement is finalized.",
        sourceUrl: "https://www.futurefamily.com/faq",
      }),
      providerClaim({
        id: "future-family-availability",
        kind: "availability",
        label: "Availability",
        value: "Plan availability and clinic participation can change.",
        sourceUrl: "https://www.futurefamily.com",
      }),
      providerClaim({
        id: "future-family-eligibility",
        kind: "eligibility",
        label: "Approval",
        value:
          "Confirm current approval requirements, rates, and fees before applying.",
        sourceUrl: "https://www.futurefamily.com",
      }),
    ],
  },
  {
    id: "prosper",
    name: "Prosper Healthcare Lending",
    productType: "personal-loan",
    productLabel: "Unsecured personal loan",
    url: "https://www.prosper.com/personal-loans",
    summary: providerClaim({
      id: "prosper-summary",
      kind: "summary",
      label: "What it is",
      value: "Unsecured personal loans can be used for healthcare expenses.",
      caveat: "Prosper loans are made by WebBank and are subject to approval.",
      sourceUrl: "https://www.prosper.com/personal-loans",
    }),
    claims: [
      providerClaim({
        id: "prosper-loan-maximum",
        kind: "loan-maximum",
        label: "Loan amount",
        value: "$2,000–$50,000.",
        caveat:
          "Eligibility is not guaranteed and depends on financial history, credit, income, expenses, and other factors.",
        sourceUrl: "https://www.prosper.com/personal-loans",
      }),
      providerClaim({
        id: "prosper-apr",
        kind: "apr",
        label: "APR",
        value: "8.99%–35.99% APR.",
        caveat:
          "Your actual rate depends on creditworthiness and the offer you receive.",
        sourceUrl: "https://www.prosper.com/personal-loans",
      }),
      providerClaim({
        id: "prosper-fee",
        kind: "fee",
        label: "Origination fee",
        value: "1%–9.99%.",
        caveat:
          "Review the loan offer and agreement for the fees that apply to you.",
        sourceUrl: "https://www.prosper.com/personal-loans",
      }),
      providerClaim({
        id: "prosper-term",
        kind: "term",
        label: "Repayment term",
        value: "Two to six years.",
        sourceUrl: "https://www.prosper.com/personal-loans",
      }),
      providerClaim({
        id: "prosper-credit-check",
        kind: "credit-check",
        label: "Rate check",
        value: "Checking your rate will not affect your credit score.",
        caveat: "Final approval and loan terms are based on creditworthiness.",
        sourceUrl: "https://www.prosper.com/personal-loans",
      }),
    ],
  },
];

export const TAX_BENEFITS: TaxBenefit[] = [
  {
    id: "medical-expense-deduction",
    icon: "taxes",
    title: "Medical Expense Deduction",
    subtitle: "Qualifying unreimbursed expenses",
    summary: taxClaim({
      id: "medical-expense-summary",
      kind: "threshold",
      label: "Federal rule",
      value:
        "On Schedule A, only qualifying unreimbursed medical expenses above 7.5% of adjusted gross income (AGI) may be deductible when you itemize.",
      caveat:
        "AGI is the income figure on your tax return. For example, $100,000 of AGI creates a $7,500 threshold; reimbursement or insurance coverage can affect what qualifies.",
      sourceUrl: "https://www.irs.gov/publications/p502",
      effectiveYear: 2025,
    }),
    eligible: [
      taxClaim({
        id: "medical-expense-fertility",
        kind: "qualifying-expense",
        label: "Fertility enhancement",
        value:
          "Some IVF and other fertility-enhancement expenses may qualify as medical care.",
        caveat:
          "Confirm that the expense is qualifying, unreimbursed, and paid for an eligible person.",
        sourceUrl: "https://www.irs.gov/publications/p502",
        effectiveYear: 2025,
      }),
      taxClaim({
        id: "medical-expense-travel",
        kind: "qualifying-expense",
        label: "Transportation",
        value:
          "Transportation primarily and essential to medical care may qualify; travel and lodging limits apply.",
        sourceUrl: "https://www.irs.gov/publications/p502",
        effectiveYear: 2025,
      }),
      taxClaim({
        id: "medical-expense-filing",
        kind: "account-rule",
        label: "How to claim",
        value:
          "Keep records and report an eligible deduction on Schedule A when you itemize.",
        sourceUrl: "https://www.irs.gov/publications/p502",
        effectiveYear: 2025,
      }),
    ],
    note: "This is educational information, not a calculation of your tax return.",
  },
  {
    id: "hsa-fsa",
    icon: "piggyBank",
    title: "HSA / FSA Contributions",
    subtitle: "Tax advantages for qualified medical expenses",
    summary: taxClaim({
      id: "hsa-fsa-summary",
      kind: "summary",
      label: "How they work",
      value:
        "Qualified HSA and health FSA contributions or reimbursements may receive tax advantages.",
      caveat: "Account eligibility, qualified expenses, and plan rules apply.",
      sourceUrl: "https://www.irs.gov/publications/p969",
      effectiveYear: 2025,
    }),
    eligible: [
      taxClaim({
        id: "hsa-fsa-hsa",
        kind: "account-rule",
        label: "HSA",
        value:
          "Eligible HSA contributions may be deductible, and qualified medical distributions may be tax-free.",
        sourceUrl: "https://www.irs.gov/publications/p969",
        effectiveYear: 2025,
      }),
      taxClaim({
        id: "hsa-fsa-fsa",
        kind: "account-rule",
        label: "Health FSA",
        value:
          "Health FSA contributions and reimbursements for qualified medical expenses may be excluded from income.",
        sourceUrl: "https://www.irs.gov/publications/p969",
        effectiveYear: 2025,
      }),
      taxClaim({
        id: "hsa-fsa-plan-rules",
        kind: "eligibility",
        label: "Plan rules",
        value:
          "Ask your plan administrator which expenses are eligible before paying or submitting a claim.",
        sourceUrl: "https://www.irs.gov/publications/p969",
        effectiveYear: 2025,
      }),
    ],
    note: "Account eligibility, qualified expenses, and plan rules apply.",
  },
];

export function currentIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function isFinancialClaimCurrent(
  claim: FinancialClaim,
  today = currentIsoDate(),
): boolean {
  const expiry = Date.parse(`${claim.provenance.reviewExpiresOn}T23:59:59Z`);
  const now = Date.parse(`${today}T00:00:00Z`);
  return Number.isFinite(expiry) && Number.isFinite(now) && expiry >= now;
}

export function currentFinancingProviders(
  today = currentIsoDate(),
): FinancingProvider[] {
  return FINANCING_PROVIDERS.filter((provider) =>
    isFinancialClaimCurrent(provider.summary, today),
  );
}

export function hasCurrentPromotionalFinancing(
  today = currentIsoDate(),
): boolean {
  return currentFinancingProviders(today).some((provider) =>
    provider.claims.some(
      (claim) =>
        claim.kind === "promotion" && isFinancialClaimCurrent(claim, today),
    ),
  );
}

export function claimEffectiveLabel(claim: FinancialClaim): string {
  const { effectiveYear, effectiveDate } = claim.provenance;
  if (effectiveYear) return `Effective ${effectiveYear}`;
  if (effectiveDate) return `Effective ${effectiveDate}`;
  return "Effective date not provided";
}
